import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import QrPanel from '../components/QrPanel';
import FeedbackSettingsModal from '../components/FeedbackSettingsModal';
import { FEEDBACK_STATUS, TEMPLATE_NAMES, MODE_NAMES } from '../utils/feedback';
import { formatWindow } from '../utils/attendance';

// Average out of 5 as a bar, with the counts of each rating underneath.
const RatingBar = ({ average, counts, na, answered }) => (
  <div style={{ minWidth: '220px' }}>
    <div className="flex flex-center gap-2">
      <div style={{ flex: 1, height: '8px', background: '#f3f4f6', borderRadius: '4px' }}>
        <div style={{
          width: `${((average || 0) / 5) * 100}%`, height: '100%', borderRadius: '4px',
          background: average >= 4 ? '#10b981' : average >= 3 ? '#f59e0b' : '#ef4444'
        }} />
      </div>
      <strong style={{ width: '36px', textAlign: 'right' }}>{average ?? '–'}</strong>
    </div>
    <div className="text-muted" style={{ fontSize: '11px', marginTop: '2px' }}>
      {counts.map((c, i) => `${i + 1}★ ${c}`).join(' · ')}{na ? ` · N/A ${na}` : ''} · {answered} answered
    </div>
  </div>
);

const Results = ({ formId, form, onChanged }) => {
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get(`/feedback/${formId}/results`)
      .then(r => setResults(r.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load results'));
  }, [formId]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const response = await api.get(`/feedback/${formId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Feedback_${form.title.replace(/[^a-zA-Z0-9]+/g, '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download feedback');
    }
  };

  const removeResponse = async (r) => {
    if (!window.confirm(`Remove this response${r.name ? ` from ${r.name}` : ''}? This cannot be undone.`)) return;
    try {
      await api.delete(`/feedback/${formId}/responses/${r.id}`);
      load();
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove');
    }
  };

  if (error) return <div className="alert alert-error mt-4">{error}</div>;
  if (!results) return <div className="text-center mt-4">Loading results...</div>;

  const anonymous = form.identity_mode === 'anonymous';

  return (
    <>
      <div className="flex flex-between flex-center mt-5 mb-3">
        <h2 style={{ margin: 0 }}>Results ({results.response_count} response{results.response_count === 1 ? '' : 's'})</h2>
        <button className="btn btn-success btn-sm" onClick={download} disabled={!results.response_count}>Download Excel</button>
      </div>
      {results.response_count === 0 ? (
        <div className="card text-muted text-center">No responses yet.</div>
      ) : (
        <>
          {/* Satisfaction score: average of the rated statements (not the overall rating or trainers). */}
          {results.score?.overall != null && (
            <div className="card mb-3 flex gap-4" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ minWidth: '160px' }}>
                <div className="text-muted text-sm">Overall satisfaction</div>
                <div style={{ fontSize: '40px', fontWeight: 700, lineHeight: 1.1, color: '#12384a' }}>
                  {results.score.overall}<span className="text-muted" style={{ fontSize: '18px', fontWeight: 400 }}> / 5</span>
                </div>
              </div>
              <div className="flex gap-2" style={{ flexWrap: 'wrap', flex: 1 }}>
                {results.score.sections.map(s => (
                  <div key={s.title} style={{ background: '#f3f4f6', borderRadius: '8px', padding: '8px 12px' }}>
                    <div className="text-muted" style={{ fontSize: '12px' }}>{s.title}</div>
                    <strong>{s.average ?? '–'}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {results.sections.map(section => {
            const rated = section.items.filter(i => i.type !== 'text');
            if (!rated.length) return null;
            return (
              <div key={section.title} className="card mb-3" style={{ padding: 0 }}>
                <h3 style={{ margin: 0, padding: '14px 20px' }}>{section.title}</h3>
                <table className="table">
                  <tbody>
                    {rated.map(item => (
                      <tr key={item.key}>
                        <td>{item.text}</td>
                        <td style={{ width: '40%' }}>
                          {item.type === 'scale'
                            ? <RatingBar {...item} />
                            : <span className="text-sm">{item.options.map((o, i) => `${o}: ${item.counts[i]}`).join(' · ')}</span>}
                          {item.followUp && item.followUp.counts.some(Boolean) && (
                            <div className="text-sm mt-1">
                              {item.followUp.text} {item.followUp.options.map((o, i) => `${o}: ${item.followUp.counts[i]}`).join(' · ')}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {results.trainers.length > 0 && (
            <div className="card mb-3" style={{ padding: 0 }}>
              <h3 style={{ margin: 0, padding: '14px 20px' }}>Rating per trainer</h3>
              <table className="table">
                <tbody>
                  {results.trainers.map(t => (
                    <tr key={t.name}>
                      <td style={{ verticalAlign: 'top' }}>
                        <strong>{t.name}</strong>
                        {t.comments.length > 0 && (
                          <div className="mt-2">
                            {t.comments.map((c, i) => (
                              <div key={i} style={{ borderTop: i ? '1px solid #f3f4f6' : 'none', padding: '6px 0' }}>
                                <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{c.text}</div>
                                {c.name && <div className="text-muted text-sm">— {c.name}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ width: '40%', verticalAlign: 'top' }}><RatingBar {...t} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {results.sections.flatMap(s => s.items).filter(i => i.type === 'text').map(item => (
            <div key={item.key} className="card mb-3">
              <h3 style={{ marginTop: 0 }}>{item.text} <span className="text-muted text-sm">({item.comments.length})</span></h3>
              {item.comments.length === 0
                ? <p className="text-muted text-sm">No comments.</p>
                : item.comments.map((c, i) => (
                    <div key={i} style={{ borderTop: i ? '1px solid #f3f4f6' : 'none', padding: '8px 0' }}>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
                      {c.name && <div className="text-muted text-sm">— {c.name}</div>}
                    </div>
                  ))}
            </div>
          ))}

          <div className="card" style={{ padding: 0 }}>
            <h3 style={{ margin: 0, padding: '14px 20px' }}>Responses</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  {!anonymous && <><th>Name</th><th>Email</th></>}
                  <th>Site/Location</th><th>Function</th><th>Submitted</th><th></th>
                </tr>
              </thead>
              <tbody>
                {results.responses.map((r, i) => (
                  <tr key={r.id}>
                    <td>{i + 1}</td>
                    {!anonymous && <><td>{r.name || '—'}</td><td className="text-sm">{r.email || '—'}</td></>}
                    <td className="text-sm">{r.site || '—'}</td>
                    <td className="text-sm">{r.function || '—'}</td>
                    <td className="text-sm nowrap">{new Date(r.submitted_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => removeResponse(r)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

const FeedbackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/feedback/${id}`);
      setForm(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load feedback form');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!form) {
    return error
      ? <div className="alert alert-error">{error}</div>
      : <div className="text-center mt-5">Loading...</div>;
  }

  const isAdmin = user?.role === 'admin';
  const link = `${window.location.origin}/f/${form.token}`;
  const status = FEEDBACK_STATUS[form.status];
  const back = form.training_id ? `/attendance/trainings/${form.training_id}` : '/feedback';

  const remove = async () => {
    if (!window.confirm(`Delete "${form.title}"${form.response_count ? ` and its ${form.response_count} response(s)` : ''}? This cannot be undone.`)) return;
    try {
      await api.delete(`/feedback/${id}`);
      navigate(back);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const windowText = form.starts_at || form.ends_at
    ? (form.starts_at && form.ends_at ? formatWindow(form.starts_at, form.ends_at)
      : form.starts_at ? `Opens ${new Date(form.starts_at).toLocaleString('en-GB')}` : `Closes ${new Date(form.ends_at).toLocaleString('en-GB')}`)
    : 'Open until a closing time is set';

  return (
    <div>
      <Link to={back} className="text-muted text-sm">← {form.training_title || 'All feedback forms'}</Link>
      <div className="flex flex-between flex-center mb-4 mt-2">
        <div>
          <h1 style={{ marginBottom: '4px' }}>{form.title}</h1>
          <p className="text-muted">
            {TEMPLATE_NAMES[form.template]} questions · {MODE_NAMES[form.identity_mode]}
            {form.category_name && ` · ${form.category_name}`}
            {' '}<span className={`badge ${status.className}`}>{status.label}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Edit</button>
          {(isAdmin || form.response_count === 0) && (
            <button className="btn btn-danger btn-sm" onClick={remove}>Delete</button>
          )}
        </div>
      </div>

      <div className="grid grid-2" style={{ alignItems: 'start' }}>
        <QrPanel
          link={link}
          title={form.title}
          subtitle={form.training_title || ''}
          instruction="Scan with your phone camera to give your feedback"
          fileName={`Feedback_QR_${form.title}`}
        />
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Settings</h3>
          <table className="table" style={{ fontSize: '14px' }}>
            <tbody>
              <tr><td className="text-muted">Window</td><td>{windowText}</td></tr>
              <tr><td className="text-muted">Names</td><td>{MODE_NAMES[form.identity_mode]}</td></tr>
              <tr><td className="text-muted">Trainers rated</td><td>{form.trainer_names.join(', ') || '—'}</td></tr>
              <tr><td className="text-muted">Site/Location</td><td>{form.site_options ? `List of ${form.site_options.length}` : 'Free text'}</td></tr>
              <tr><td className="text-muted">Function</td><td>{form.function_options ? `List of ${form.function_options.length}` : 'Free text'}</td></tr>
              <tr><td className="text-muted">Responses</td><td><strong>{form.response_count}</strong></td></tr>
            </tbody>
          </table>
          {!isAdmin && <p className="text-muted text-sm mt-2">Responses are visible to admins only.</p>}
        </div>
      </div>

      {isAdmin && <Results formId={id} form={form} onChanged={load} />}

      {editing && (
        <FeedbackSettingsModal
          form={form}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
};

export default FeedbackDetail;
